import { useEffect, useState } from 'react';
import {
  AppBar, Toolbar, Box, Button, IconButton,
  Drawer, List, ListItem, ListItemText, ListItemIcon,
  Container, Chip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { NAVY, NAVY_LIGHT, GREEN, GREEN_DARK, PHONE, PHONE_DIGITS } from '../theme';
import logo from '../logo.png';

const NAV = [
  { label: 'Why RideX', href: '/#why', icon: VerifiedUserIcon },
  { label: 'Cost Breakdown', href: '/#cost-breakdown', icon: ReceiptLongIcon },
  { label: 'ROI Plans', href: '/#roi-plans', icon: TrendingUpIcon, highlight: true },
  { label: 'Contact', href: '/#contact', icon: PhoneIcon },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: scrolled ? '1px solid rgba(11, 42, 92, 0.1)' : '1px solid rgba(11, 42, 92, 0.06)',
          boxShadow: scrolled
            ? '0 10px 30px rgba(11, 42, 92, 0.08), 0 1px 3px rgba(0,0,0,0.05)'
            : '0 4px 16px rgba(11, 42, 92, 0.03)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Top subtle brand gradient line */}
        <Box
          sx={{
            height: 3,
            background: `linear-gradient(90deg, ${NAVY} 0%, ${NAVY_LIGHT} 50%, ${GREEN} 100%)`,
          }}
        />

        <Container maxWidth="xl" disableGutters sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 64, md: 74 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            {/* Logo with blue & green brand colors */}
            <Box
              component="a"
              href="/#top"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                flexShrink: 0,
                py: 0.5,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="RideX City"
                sx={{
                  height: { xs: 34, sm: 38, md: 44 },
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  transition: 'transform 0.25s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                }}
              />
            </Box>

            {/* Desktop Navigation Links & Action buttons */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: { md: 1, lg: 1.5 },
                flexShrink: 0,
              }}
            >
              {NAV.map((n) => {
                if (n.highlight) {
                  return (
                    <Button
                      key={n.label}
                      href={n.href}
                      startIcon={<TrendingUpIcon sx={{ fontSize: 18, color: GREEN_DARK }} />}
                      sx={{
                        color: NAVY,
                        fontWeight: 700,
                        fontSize: '0.92rem',
                        whiteSpace: 'nowrap',
                        px: 1.8,
                        py: 0.8,
                        borderRadius: 2,
                        bgcolor: alpha(GREEN, 0.1),
                        border: `1px solid ${alpha(GREEN, 0.3)}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(GREEN, 0.18),
                          borderColor: GREEN,
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      {n.label}
                    </Button>
                  );
                }
                return (
                  <Button
                    key={n.label}
                    href={n.href}
                    sx={{
                      color: NAVY,
                      fontWeight: 600,
                      fontSize: '0.92rem',
                      whiteSpace: 'nowrap',
                      px: 1.6,
                      py: 0.8,
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: GREEN_DARK,
                        bgcolor: 'rgba(11, 42, 92, 0.05)',
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {n.label}
                  </Button>
                );
              })}

              {/* Vertical divider line */}
              <Box
                sx={{
                  width: '1px',
                  height: 24,
                  bgcolor: 'rgba(11, 42, 92, 0.12)',
                  mx: { md: 0.5, lg: 1 },
                  flexShrink: 0,
                }}
              />

              <Button
                variant="outlined"
                size="medium"
                href={`https://wa.me/${PHONE_DIGITS}?text=Hi%2C%20interested%20in%20RideX%20City%20investment`}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
                sx={{
                  color: NAVY,
                  borderColor: 'rgba(11, 42, 92, 0.2)',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  whiteSpace: 'nowrap',
                  px: 1.8,
                  py: 0.8,
                  borderRadius: 2,
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#25D366',
                    bgcolor: 'rgba(37, 211, 102, 0.08)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                WhatsApp
              </Button>

              <Button
                variant="contained"
                size="medium"
                href={`tel:${PHONE}`}
                startIcon={<PhoneIcon sx={{ fontSize: 18 }} />}
                sx={{
                  bgcolor: GREEN,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.92rem',
                  whiteSpace: 'nowrap',
                  px: 2.4,
                  py: 0.85,
                  borderRadius: 2,
                  flexShrink: 0,
                  boxShadow: `0 4px 14px ${alpha(GREEN, 0.4)}`,
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    bgcolor: GREEN_DARK,
                    boxShadow: `0 6px 20px ${alpha(GREEN, 0.55)}`,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {PHONE}
              </Button>
            </Box>

            {/* Mobile Hamburger Menu button */}
            <IconButton
              sx={{
                display: { md: 'none' },
                color: NAVY,
                bgcolor: 'rgba(11, 42, 92, 0.05)',
                border: '1px solid rgba(11, 42, 92, 0.1)',
                p: 1,
                borderRadius: 2,
                '&:hover': { bgcolor: 'rgba(11, 42, 92, 0.1)' },
              }}
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: 290, sm: 320 },
            bgcolor: '#ffffff',
            boxShadow: '-8px 0 32px rgba(11, 42, 92, 0.16)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Drawer Header with Blue Logo */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(11, 42, 92, 0.08)',
          }}
        >
          <Box component="a" href="/#top" onClick={() => setOpen(false)} sx={{ display: 'flex' }}>
            <Box
              component="img"
              src={logo}
              alt="RideX City"
              sx={{ height: 34, objectFit: 'contain' }}
            />
          </Box>
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              color: NAVY,
              bgcolor: 'rgba(11, 42, 92, 0.05)',
              '&:hover': { bgcolor: 'rgba(11, 42, 92, 0.1)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Drawer Navigation List */}
        <List sx={{ px: 1.5, pt: 2 }}>
          {NAV.map((n) => {
            const IconComponent = n.icon;
            return (
              <ListItem
                key={n.label}
                component="a"
                href={n.href}
                onClick={() => setOpen(false)}
                sx={{
                  borderRadius: 2,
                  mb: 0.8,
                  px: 1.5,
                  py: 1,
                  color: NAVY,
                  bgcolor: n.highlight ? alpha(GREEN, 0.08) : 'transparent',
                  border: n.highlight ? `1px solid ${alpha(GREEN, 0.25)}` : '1px solid transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: alpha(NAVY, 0.05),
                    color: GREEN_DARK,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 34, color: n.highlight ? GREEN_DARK : NAVY_LIGHT }}>
                  <IconComponent fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={n.label}
                  primaryTypographyProps={{
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: 'inherit',
                  }}
                />
                {n.highlight && (
                  <Chip
                    label="₹3k/mo"
                    size="small"
                    sx={{
                      bgcolor: GREEN,
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: '0.68rem',
                      height: 20,
                    }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>

        {/* Bottom CTA Block in Drawer */}
        <Box
          sx={{
            p: 2,
            mt: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.2,
            bgcolor: '#F8FAFC',
            borderTop: '1px solid rgba(11, 42, 92, 0.08)',
          }}
        >
          <Button
            fullWidth
            variant="contained"
            href={`tel:${PHONE}`}
            startIcon={<PhoneIcon />}
            sx={{
              bgcolor: GREEN,
              color: '#fff',
              '&:hover': { bgcolor: GREEN_DARK },
              py: 1.3,
              fontWeight: 700,
              borderRadius: 2,
              boxShadow: `0 4px 14px ${alpha(GREEN, 0.35)}`,
            }}
          >
            Call {PHONE}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            href={`https://wa.me/${PHONE_DIGITS}?text=Hi%2C%20interested%20in%20RideX%20City%20investment`}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
            sx={{
              color: NAVY,
              borderColor: 'rgba(11, 42, 92, 0.2)',
              bgcolor: '#fff',
              py: 1.2,
              fontWeight: 600,
              borderRadius: 2,
              '&:hover': {
                borderColor: '#25D366',
                bgcolor: 'rgba(37, 211, 102, 0.06)',
              },
            }}
          >
            WhatsApp Us
          </Button>
        </Box>
      </Drawer>
    </>
  );
}

